import {
  Button,
  Inline,
  Modal,
  type ModalProps,
  notifySuccess,
  Skeleton,
} from '@omnipresentgroup/design-system';
import { useEmployee, useEmployeeMutation } from '@omnipresentgroup/domains';
import MDEditor from '@uiw/react-md-editor';
import { useEffect, useState } from 'react';
import rehypeSanitize from 'rehype-sanitize';

type Props = {
  employeeId: string;
  modalProps: Omit<ModalProps, 'children'>;
  handleCancel: () => void;
  handleSuccess?: () => void;
  canEdit?: boolean;
};

const MODAL_TITLE = 'Employment FAQ';

export const EmployeeEmploymentInfoModal = (props: Props) => {
  const { employeeId, modalProps, handleCancel, handleSuccess, canEdit } =
    props;

  const [markdownValue, setMarkdownValue] = useState<string | undefined>();

  const { data: employee, isLoading: isLoadingEmployee } = useEmployee({
    id: employeeId,
    enabled: !!employeeId,
  });

  const { mutate: updateEmployee, isPending: isUpdatingEmployee } =
    useEmployeeMutation({
      employeeId,
      onSuccess: () => {
        notifySuccess({
          title: 'Employee information updated successfully',
        });
        handleSuccess?.();
      },
    });

  useEffect(() => {
    employee && setMarkdownValue(employee?.employmentInformation);
  }, [employee]);

  return (
    <Modal {...modalProps} span={8}>
      <Modal.Header title={MODAL_TITLE} />
      <Modal.Body>
        {isLoadingEmployee ? (
          <Skeleton height={200} />
        ) : (
          <MDEditor
            height={500}
            value={markdownValue}
            onChange={setMarkdownValue}
            previewOptions={{
              rehypePlugins: [[rehypeSanitize]],
            }}
            textareaProps={{
              disabled: !canEdit,
            }}
            preview={canEdit ? undefined : 'preview'}
            data-color-mode="light"
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isUpdatingEmployee}
          >
            Cancel
          </Button>
          {canEdit && (
            <Button
              variant="primary"
              disabled={isLoadingEmployee}
              loading={isUpdatingEmployee}
              onClick={() =>
                updateEmployee({ employmentInformation: markdownValue })
              }
            >
              Update
            </Button>
          )}
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
